import { useState } from 'react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import { extractErrorMessage } from '../../api/errors';
import { resolveStyle } from '../../lib/email-design';
import { useActivateEmailTheme, type EmailFont, type EmailStyle, type EmailThemeVersion } from '../../queries/brandEmailTheme';

const DATE_FORMATTER = new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

interface EmailVersionHistoryProps {
  versions: EmailThemeVersion[];
  fonts: EmailFont[];
  starterStyle: EmailStyle;
  canWrite: boolean;
  brandName: string;
  /** Called once a restore is live, so unsaved edits based on the old design are dropped. */
  onRestored: () => void;
}

export function EmailVersionHistory({ versions, fonts, starterStyle, canWrite, brandName, onRestored }: EmailVersionHistoryProps) {
  const activate = useActivateEmailTheme();
  const toast = useToast();
  const [restoring, setRestoring] = useState<EmailThemeVersion | null>(null);

  if (versions.length === 0) return null;

  // Short font name: the label without its "(falls back to …)" note.
  const fontName = (key: string) => (fonts.find((f) => f.key === key)?.label ?? key).replace(/\s*\(.*\)$/, '');
  const styleSummary = (v: EmailThemeVersion) => {
    const style = resolveStyle(v.style, starterStyle);
    const typefaces = style.headingFont === style.bodyFont ? fontName(style.headingFont) : `${fontName(style.headingFont)} / ${fontName(style.bodyFont)}`;
    return `${typefaces}, ${style.headlineCase === 'upper' ? 'capitals' : 'sentence case'}`;
  };

  const confirm = () => {
    if (!restoring) return;
    const { version } = restoring;
    activate.mutate(version, {
      onSuccess: () => {
        toast.success(`Email design version ${version} is live for ${brandName}.`);
        onRestored();
        setRestoring(null);
      },
      onError: (err) => {
        toast.error(extractErrorMessage(err, 'Failed to restore the design.'));
        setRestoring(null);
      },
    });
  };

  return (
    <div>
      <h4 className="mb-2 text-sm font-medium">Version history</h4>
      <ul className="divide-y divide-border rounded-md border border-border">
        {versions.map((v) => (
          <li key={v.version} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
            <span className="font-medium">Version {v.version}</span>
            {v.isActive && <Badge variant="success">Live</Badge>}
            <span className="flex gap-0.5" aria-hidden>
              {[v.tokens.card, v.tokens.heading, v.tokens.accent, v.tokens.button].map((colour, i) => (
                <span key={i} className="inline-block h-3 w-3 rounded-sm border border-border" style={{ backgroundColor: colour }} />
              ))}
            </span>
            <span className="text-xs text-muted-foreground">{styleSummary(v)}</span>
            <span className="text-xs text-muted-foreground">
              {DATE_FORMATTER.format(new Date(v.createdAt))}
              {v.createdBy ? ` by ${v.createdBy}` : ''}
            </span>
            {canWrite && !v.isActive && (
              <Button size="sm" variant="outline" className="ml-auto" onClick={() => setRestoring(v)}>
                Restore
              </Button>
            )}
          </li>
        ))}
      </ul>

      <ConfirmDialog
        open={restoring !== null}
        title={restoring ? `Restore email design version ${restoring.version}?` : ''}
        description={
          restoring
            ? `Every email ${brandName} sends will use version ${restoring.version} from now on. The current version stays in the history.`
            : ''
        }
        confirmLabel="Restore"
        busy={activate.isPending}
        onConfirm={confirm}
        onOpenChange={(open) => !open && !activate.isPending && setRestoring(null)}
      />
    </div>
  );
}
